import React from 'react';
import { FaTable, FaCheck, FaChevronDown, FaChevronRight, FaHistory } from 'react-icons/fa';
import { Badge } from './Primitives';
import ChangeSourceBadges from './ChangeSourceBadges';
import GenerationStatusBadge from './GenerationStatusBadge';
import ChangeDetailPanel from './ChangeDetailPanel';
import { formatDateTime, recentGenStatus } from '../utils/format';

/**
 * Scheda di una macrofunzione recente.
 *
 * Struttura accessibile: due controlli SORELLA (mai annidati) —
 * un pulsante di selezione e un pulsante di espansione con
 * aria-expanded/aria-controls — più la regione dettaglio collassabile.
 */
export default function MacroFunctionCard({
  item,
  selected,
  onToggleSelect,
  expanded,
  onToggleExpand,
  detail,
  onRetryDetail,
}) {
  const detailId = `gb-macro-detail-${item.id}`;
  const status = recentGenStatus(item);
  const gen = item.generation || {};

  return (
    <li
      className={`overflow-hidden rounded-xl border transition-all ${
        selected
          ? 'border-primary bg-primary/5 dark:bg-primary/10'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div className="flex items-stretch">
        {/* Controllo di selezione (pulsante) */}
        <button
          type="button"
          onClick={() => onToggleSelect(item.id)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Deseleziona' : 'Seleziona'} ${item.name || `macro ${item.id}`}`}
          className="flex min-w-0 flex-1 items-start gap-3 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all ${
              selected
                ? 'border-primary bg-primary text-white'
                : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-900'
            }`}
            aria-hidden="true"
          >
            {selected && <FaCheck size={10} />}
          </span>

          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate font-medium text-gray-800 dark:text-gray-100">
                {item.name || `Macro ${item.id}`}
              </span>
              {item.moduleCode && (
                <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {item.moduleCode}
                </Badge>
              )}
              {item.isMine && (
                <Badge className="bg-primary/10 text-primary dark:bg-primary/20">Mia</Badge>
              )}
              {status && <GenerationStatusBadge status={status} />}
            </span>

            {item.description && (
              <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                {item.description}
              </span>
            )}

            <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {item.tableName && (
                <span className="inline-flex items-center gap-1">
                  <FaTable size={11} className="text-gray-400" />
                  {item.tableName}
                </span>
              )}
              <span>Modificato: {formatDateTime(item.effectiveModifiedAt)}</span>
              {gen.lastSuccessfulAt && (
                <span className="inline-flex items-center gap-1">
                  <FaHistory size={10} className="text-gray-400" />
                  Ultima generazione: {formatDateTime(gen.lastSuccessfulAt)}
                </span>
              )}
              {gen.successfulSkeletonCount != null && gen.successfulSkeletonCount > 0 && (
                <span>{gen.successfulSkeletonCount} scheletri generati</span>
              )}
            </span>

            <span className="mt-1.5 block">
              <ChangeSourceBadges item={item} />
            </span>
          </span>
        </button>

        {/* Controllo di espansione (pulsante sorella, non annidato) */}
        <button
          type="button"
          onClick={() => onToggleExpand(item.id)}
          aria-expanded={expanded}
          aria-controls={detailId}
          className="flex shrink-0 items-center gap-1.5 border-l border-gray-200 px-3 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/50"
        >
          {expanded ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
          <span className="hidden sm:inline">Dettagli</span>
        </button>
      </div>

      {/* Regione dettaglio collassabile */}
      {expanded && (
        <div
          id={detailId}
          role="region"
          aria-label={`Dettaglio modifiche ${item.name || item.id}`}
          className="border-t border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/40"
        >
          <ChangeDetailPanel detail={detail} onRetry={() => onRetryDetail(item.id)} />
        </div>
      )}
    </li>
  );
}
